import {
  handleErrorResponse,
  handleGenericCatchError,
} from "@/utils/functions";
import {
  ConversationRequest,
  ConversationResponse,
  ConversationCreateResponse,
} from "@/api-client";
import { FaX } from "react-icons/fa6";
import logoSrc from "/logo-tradepeg.png";
import Pusher, { Channel } from "pusher-js";
import { AssistantMessage } from "./AiMessage";
import { useState, useRef, useEffect } from "react";
import { ReportsService } from "@/api-client/sdk.gen";
import { Send, User, Loader2, Plus } from "lucide-react";
import { useUserInfoStore } from "@/utils/store/userStore";
import { useTranslatorStore } from "@/utils/store/translatorStore";

export interface Message extends ConversationResponse {
  role: "user" | "assistant";
  color?: string;
}

export const AiChatBox = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState<string>("");
  const [convResponse, setConvResponse] = useState<
    ConversationCreateResponse | undefined
  >(undefined);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingNewConversation, setIsLoadingNewConversation] =
    useState<boolean>(false);
  const [hasStarted, setHasStarted] = useState<boolean>(false);
  const [showDisclaimer, setShowDisclaimer] = useState<boolean>(true);
  const [tempStatus, setTempStatus] = useState<{
    status: string;
    color: string;
  } | null>(null);

  const pusherRef = useRef<Pusher | null>(null);
  const channelRef = useRef<Channel | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { t } = useTranslatorStore();
  const { userInfo } = useUserInfoStore();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const assistantName = "TradePeg AI Assistant";

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleStartConversation = async () => {
    try {
      setIsLoadingNewConversation(true);
      const { data, error } = await ReportsService.aiConversationCreate();
      if (error) {
        handleErrorResponse(error);
        return;
      }
      setConvResponse(data.data);
      initializePusherChannel(data?.data?.sessionId || null);
      setHasStarted(true);
      setTimeout(() => textareaRef.current?.focus(), 100);
    } catch (error) {
      handleGenericCatchError(error);
    } finally {
      setIsLoadingNewConversation(false);
    }
  };

  const handleNewConversation = async () => {
    setMessages([]);
    setInput("");
    setConvResponse(undefined);
    await handleStartConversation();
  };

  const initializePusherChannel = (sessionId: string | null) => {
    if (!sessionId) return;
    if (!userInfo?.settings?.pusher) return;

    // Initialize Pusher only once
    if (!pusherRef.current) {
      const pusherSettings = userInfo.settings.pusher;
      pusherRef.current = new Pusher(pusherSettings?.config?.key!, {
        cluster: pusherSettings?.config?.cluster!,
      });
    }

    // Unsubscribe from previous channel if exists
    if (channelRef.current) {
      channelRef.current.unbind_all();
      pusherRef.current.unsubscribe(channelRef.current.name);
    }

    const channelName = `conv-${sessionId}`;
    const channel = pusherRef.current.subscribe(channelName);
    channelRef.current = channel;

    channel.bind("statusUpdate", function (messageData: any) {
      const status = messageData.status;
      const color = messageData.color || "blue";
      setTempStatus({ status, color });
    });
  };

  const handleSendMessage = async () => {
    if (!input.trim() || isLoading || !convResponse) return;

    const userMessage = input.trim();
    const body: ConversationRequest = {
      sessionId: convResponse.sessionId,
      question: userMessage,
    };

    setInput("");
    setMessages((prevMessages) => [
      ...prevMessages,
      {
        role: "user",
        messageId: `user-${Date.now()}`,
        queryResult: { data: userMessage },
        sessionId: convResponse.sessionId,
      },
    ]);

    try {
      setIsLoading(true);

      const { data, error } = await ReportsService.aiConversationMessage({
        body,
      });

      const message = data?.data;

      if (error) {
        handleErrorResponse(error);
        return;
      }

      setMessages((prevMessages) => [
        ...prevMessages,
        {
          ...message,
          role: "assistant",
          sessionId: message?.sessionId || convResponse.sessionId,
          messageId: message?.messageId || `assistant-${Date.now()}`,
        },
      ]);
      setConvResponse((prev) =>
        prev
          ? {
              ...prev,
              remainingQuota: message?.remainingQuota ?? prev.remainingQuota,
            }
          : prev
      );
    } catch (error) {
      handleGenericCatchError(error);
    } finally {
      setIsLoading(false);
      setTempStatus(null);
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (!hasStarted) {
    return (
      <div className="flex h-screen w-full flex-col items-center justify-center bg-gradient-to-b from-gray-50 to-white">
        <div className="flex flex-col items-center gap-4 px-4 text-center">
          <img src={logoSrc} alt="TradePeg Logo" className="w-24" />

          <div className="">
            <h1 className="mb-2 text-4xl font-semibold text-gray-900">
              Welcome to {assistantName}
            </h1>
            <p className="text-lg text-gray-600">
              Start a conversation to {t("analyse")} your data
            </p>
          </div>
          <button
            onClick={handleStartConversation}
            disabled={isLoadingNewConversation}
            className="flex w-[240px] items-center justify-center gap-2 rounded-full bg-[#02c161] px-8 py-4 text-lg font-semibold text-white shadow-lg transition-all hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isLoadingNewConversation ? (
              <>
                <Loader2 className="h-6 w-6 animate-spin" />
                Starting...
              </>
            ) : (
              <>Start Conversation</>
            )}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen w-full flex-col bg-white">
      <div className="border-b border-gray-200 bg-white px-4 py-3">
        <div className="mx-auto flex max-w-3xl items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={logoSrc} alt="TradePeg Logo" className="w-8" />
            <div>
              <h1 className="-mb-1 text-lg font-semibold text-gray-900">
                {assistantName}
              </h1>
              {convResponse?.remainingQuota !== undefined && (
                <p className="text-xs text-gray-600">
                  {`Remaining quota: ${convResponse.remainingQuota}`}
                </p>
              )}
            </div>
          </div>
          {hasStarted && (
            <button
              onClick={handleNewConversation}
              disabled={isLoading}
              className="flex items-center gap-2 rounded-lg border border-gray-300 bg-green-500 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Plus className="h-5 w-5" />
              New Conversation
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-3xl px-4 py-8">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-6 pt-5">
              <div className="space-y-2">
                {convResponse?.disclaimer?.disclaimer && showDisclaimer && (
                  <div className="relative rounded-lg border border-l-4 border-gray-200 border-l-gray-400 bg-gray-50 p-4 py-2">
                    <button
                      onClick={() => setShowDisclaimer(false)}
                      className="absolute right-2 top-2 text-gray-600 hover:text-gray-700"
                    >
                      <FaX size={14} />
                    </button>
                    <span className="whitespace-pre-wrap text-sm text-gray-800">
                      {convResponse.disclaimer.disclaimer}
                    </span>
                  </div>
                )}
                <h2 className="pt-20 text-center text-2xl font-semibold text-gray-900">
                  How can I help you today?
                </h2>
                <p className="text-center text-gray-600">
                  Ask me anything about your data
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {messages.map((message, index) => (
                <div key={message.messageId || index} className="group">
                  {message.role === "user" ? (
                    <div className="flex gap-4">
                      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-500">
                        <User className="h-5 w-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-gray-900">
                          You
                        </div>
                        <div className="mt-1 whitespace-pre-wrap text-gray-800">
                          {message.queryResult?.data || ""}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-4">
                      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-[#02c161] bg-white">
                        <img
                          src={logoSrc}
                          alt="TradePeg Logo"
                          className="w-4"
                        />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-gray-900">
                          {assistantName}
                        </div>
                        <div className="mt-1">
                          <AssistantMessage
                            message={message}
                            setIsLoading={setIsLoading}
                            isLoading={isLoading}
                            convResponse={convResponse}
                            setMessages={setMessages}
                            textareaRef={textareaRef}
                            setTempStatus={setTempStatus}
                            updateRemainingQuota={(
                              newQuota: number | undefined
                            ) => {
                              if (newQuota === undefined) return;
                              setConvResponse((prev) =>
                                prev
                                  ? { ...prev, remainingQuota: newQuota }
                                  : prev
                              );
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {(isLoading || tempStatus) && (
            <div className="group mt-6">
              <div className="flex gap-4">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-[#02c161] bg-white">
                  <img src={logoSrc} alt="TradePeg Logo" className="w-4" />
                </div>
                <div className="flex-1 pt-1">
                  <div className="text-sm font-semibold text-gray-900">
                    {assistantName}
                  </div>
                  {tempStatus && (
                    <div className="mt-1">
                      {(() => {
                        const color = tempStatus?.color || "blue";
                        const colorClasses: Record<
                          string,
                          {
                            bg: string;
                            border: string;
                            text: string;
                            borderL: string;
                          }
                        > = {
                          blue: {
                            bg: "bg-blue-50",
                            border: "border-blue-200",
                            text: "text-blue-800",
                            borderL: "border-l-blue-400",
                          },
                          green: {
                            bg: "bg-green-50",
                            border: "border-green-200",
                            text: "text-green-800",
                            borderL: "border-l-green-400",
                          },
                          yellow: {
                            bg: "bg-yellow-50",
                            border: "border-yellow-200",
                            text: "text-yellow-800",
                            borderL: "border-l-yellow-400",
                          },
                          red: {
                            bg: "bg-red-50",
                            border: "border-red-200",
                            text: "text-red-800",
                            borderL: "border-l-red-400",
                          },
                          purple: {
                            bg: "bg-purple-50",
                            border: "border-purple-200",
                            text: "text-purple-800",
                            borderL: "border-l-purple-400",
                          },
                          orange: {
                            bg: "bg-orange-50",
                            border: "border-orange-200",
                            text: "text-orange-800",
                            borderL: "border-l-orange-400",
                          },
                          gray: {
                            bg: "bg-gray-50",
                            border: "border-gray-200",
                            text: "text-gray-800",
                            borderL: "border-l-gray-400",
                          },
                        };
                        const currentColor =
                          colorClasses[color] || colorClasses.blue;

                        return (
                          <div
                            className={`rounded-lg border border-l-4 ${currentColor.border} ${currentColor.borderL} ${currentColor.bg} p-3`}
                          >
                            <div className="flex items-center gap-2">
                              <Loader2
                                className={`h-4 w-4 animate-spin ${currentColor.text}`}
                              />
                              <span className={`text-sm ${currentColor.text}`}>
                                {tempStatus?.status}
                              </span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                  {/*    
                  ) : (
                    <div className="mt-1 flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin text-gray-600" />
                      <span className="text-sm text-gray-600">Thinking...</span>
                    </div>
                  )} */}
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      <div className="border-t border-gray-200 bg-white">
        <div className="relative mx-auto flex max-w-3xl items-end gap-3 px-4 py-6">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder="Message..."
            autoFocus
            className="max-h-[200px] min-h-[52px] flex-1 resize-none rounded-2xl border border-gray-300 px-4 py-3 pr-12 focus:border-[#02c161] focus:outline-none focus:ring-2 focus:ring-[#02c161]/20"
            rows={2}
            disabled={isLoading}
          />
          <button
            onClick={handleSendMessage}
            disabled={!input.trim() || isLoading}
            className="absolute right-6 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg bg-[#02c161] text-white transition-all hover:shadow-md disabled:cursor-not-allowed disabled:opacity-40"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
